import type { Breakpoints, ServiceConfig } from '@/lib/types'
import { Switch } from '@/components/ui/switch'
import { Field } from '../components/fields'
import { JsonEditor, JsonInline } from '../components/json'
import { getDeep, setDeep } from '../lib/deep'

const numberInput =
  'w-32 rounded border border-slate-200 bg-white px-2 py-1 font-mono text-sm outline-none focus:border-slate-400'
const textInput =
  'rounded border border-slate-200 bg-white px-2 py-1 font-mono text-sm outline-none focus:border-slate-400'
const checkbox = 'h-4 w-4 rounded border-slate-300'

export function SettingsEditor({
  draft,
  setDraft,
}: {
  draft: ServiceConfig
  setDraft: (cfg: ServiceConfig) => void
}) {
  const prebid = draft.prebid
  const g = (...keys: string[]) => getDeep(prebid, ...keys)
  const s = (keys: string[], val: unknown) =>
    setDraft({ ...draft, prebid: setDeep(prebid, keys, val) })

  function setBp(key: keyof Breakpoints, value: number) {
    setDraft({ ...draft, breakpoints: { ...draft.breakpoints, [key]: value } })
  }

  const enabled = draft.prebidEnabled

  return (
    <div className="space-y-6">
      <section
        className={
          'flex flex-wrap items-center justify-between gap-4 rounded-lg border p-4 ' +
          (enabled ? 'border-emerald-200 bg-emerald-50/60' : 'border-rose-200 bg-rose-50/60')
        }
      >
        <div className="flex items-start gap-3">
          <span
            className={
              'mt-1.5 inline-flex size-2 shrink-0 rounded-full ' +
              (enabled ? 'bg-emerald-500' : 'bg-rose-500')
            }
          />
          <div>
            <h3
              className={
                'text-sm font-semibold ' + (enabled ? 'text-emerald-900' : 'text-rose-900')
              }
            >
              Prebid {enabled ? 'włączony' : 'wyłączony'} ({draft.service})
            </h3>
            <p className={'mt-0.5 text-xs ' + (enabled ? 'text-emerald-700' : 'text-rose-700')}>
              {enabled
                ? 'Aukcja Prebid działa normalnie. Sloty oznaczone „tylko GAM” idą bezpośrednio do GAM.'
                : 'Wszystkie sloty serwowane bezpośrednio z GAM (GPT), bez aukcji Prebid.'}
            </p>
          </div>
        </div>
        <label className="inline-flex cursor-pointer items-center gap-2 text-xs font-medium text-slate-600">
          <span>OFF</span>
          <Switch
            checked={enabled}
            onCheckedChange={(v) => setDraft({ ...draft, prebidEnabled: v })}
          />
          <span>ON</span>
        </label>
      </section>

      <section className="rounded-lg border border-slate-200 p-4">
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-600">
          Aukcja
        </h3>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {(
            [
              ['debug', 'debug'],
              ['enableTIDs', 'enableTIDs'],
              ['enableSendAllBids', 'enableSendAllBids'],
              ['useBidCache', 'useBidCache'],
            ] as const
          ).map(([key, label]) => (
            <label key={key} className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={!!g(key)}
                onChange={(e) => s([key], e.target.checked)}
                className={checkbox}
              />
              {label}
            </label>
          ))}
          <Field label="bidderTimeout (ms)">
            <input
              type="number"
              min={0}
              value={Number(g('bidderTimeout') ?? 0)}
              onChange={(e) => s(['bidderTimeout'], Number(e.target.value))}
              className={numberInput}
            />
          </Field>
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 p-4">
        <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-600">
          Breakpointy (min-width px)
        </h3>
        <p className="mb-3 text-xs text-slate-400">
          Decydują, który zestaw <code>sizes</code> ze slotu trafia do adUnitu. Porównywane
          top-down: desktop ≥ tablet ≥ mobile.
        </p>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          {(['desktop', 'tablet', 'mobile'] as const).map((key) => (
            <Field key={key} label={key}>
              <input
                type="number"
                min={0}
                value={draft.breakpoints?.[key] ?? 0}
                onChange={(e) => setBp(key, Number(e.target.value) || 0)}
                className={numberInput}
              />
            </Field>
          ))}
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 p-4">
        <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-600">GPID</h3>
        <p className="mb-3 text-xs text-slate-400">
          GPID slotu to <code>/1042598/{`<network>/<account>//<slot>`}</code>. Musi zgadzać się ze
          ścieżką ad unitu w GAM, inaczej SSP nie zmapuje inventory.
        </p>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Field label="gpidNetwork">
            <input
              value={draft.gpidNetwork ?? ''}
              onChange={(e) => setDraft({ ...draft, gpidNetwork: e.target.value })}
              placeholder="np. SpidersWeb"
              className={textInput}
            />
          </Field>
          <Field label="gpidAccount">
            <input
              value={draft.gpidAccount ?? ''}
              onChange={(e) => setDraft({ ...draft, gpidAccount: e.target.value })}
              placeholder="np. SW"
              className={textInput}
            />
          </Field>
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 p-4">
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-600">
          Waluta
        </h3>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <Field label="adServerCurrency">
            <input
              value={String(g('currency', 'adServerCurrency') ?? '')}
              onChange={(e) => s(['currency', 'adServerCurrency'], e.target.value)}
              className={textInput}
            />
          </Field>
          <Field label="granularityMultiplier">
            <input
              type="number"
              step={0.1}
              value={Number(g('currency', 'granularityMultiplier') ?? 1)}
              onChange={(e) => s(['currency', 'granularityMultiplier'], Number(e.target.value))}
              className={numberInput}
            />
          </Field>
        </div>
        <div className="mt-3">
          <Field label="defaultRates (JSON)">
            <JsonInline
              value={g('currency', 'defaultRates')}
              onChange={(v) => s(['currency', 'defaultRates'], v)}
            />
          </Field>
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 p-4">
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-600">GDPR</h3>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Field label="timeout (ms)">
            <input
              type="number"
              min={0}
              value={Number(g('consentManagement', 'gdpr', 'timeout') ?? 8000)}
              onChange={(e) => s(['consentManagement', 'gdpr', 'timeout'], Number(e.target.value))}
              className={numberInput}
            />
          </Field>
          <label className="flex items-center gap-2 pt-5 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={!!g('consentManagement', 'gdpr', 'defaultGdprScope')}
              onChange={(e) =>
                s(['consentManagement', 'gdpr', 'defaultGdprScope'], e.target.checked)
              }
              className={checkbox}
            />
            defaultGdprScope
          </label>
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 p-4">
        <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-600">
          UserSync
        </h3>
        <p className="mb-3 text-xs text-slate-400">
          Listę bidderów do iframe-syncu ustawiasz w zakładce „Dostawcy".
        </p>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <Field label="auctionDelay (ms)">
            <input
              type="number"
              min={0}
              value={Number(g('userSync', 'auctionDelay') ?? 0)}
              onChange={(e) => s(['userSync', 'auctionDelay'], Number(e.target.value))}
              className={numberInput}
            />
          </Field>
          <Field label="syncsPerBidder">
            <input
              type="number"
              min={0}
              value={Number(g('userSync', 'syncsPerBidder') ?? 3)}
              onChange={(e) => s(['userSync', 'syncsPerBidder'], Number(e.target.value))}
              className={numberInput}
            />
          </Field>
          <Field label="syncDelay (ms)">
            <input
              type="number"
              min={0}
              value={Number(g('userSync', 'syncDelay') ?? 3000)}
              onChange={(e) => s(['userSync', 'syncDelay'], Number(e.target.value))}
              className={numberInput}
            />
          </Field>
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 p-4">
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-600">
          Price granularity
        </h3>
        <JsonInline value={g('priceGranularity')} onChange={(v) => s(['priceGranularity'], v)} />
      </section>

      <section className="rounded-lg border border-slate-200 p-4">
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-600">
          Pełny config pbjs.setConfig (JSON)
        </h3>
        <JsonEditor
          label=""
          value={prebid}
          onChange={(v) => setDraft({ ...draft, prebid: v as Record<string, unknown> })}
        />
      </section>
    </div>
  )
}
